import { brandPalette } from "../../../../constants/brandPalette";
import {
  Box,
  Button,
  Flex,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  Image,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
  SimpleGrid,
  Stack,
  Switch,
  Text,
  Textarea,
  useDisclosure,
  VStack,
} from "@chakra-ui/react";
import React, { useEffect, useState, useRef } from "react";
import { useForm } from "react-hook-form";
import ReactQuill from "react-quill";
import MathQuill4Quill from 'mathquill4quill';
import { useDispatch, useSelector } from "react-redux";
import Select from "react-select";
import { toast } from "react-toastify";
import MainInput from "../../../../components/Form/input";
import MainModel from "../../../../components/Models/main-model";
import {
  adminModalBodyProps,
  adminModalContentProps,
  adminModalHeaderProps,
  adminSelectStyles,
} from "../../../../components/Models/admin-modal-theme";
import { getAllChaptersRequest } from "../../../../modules/admin-modules/chapters/Actions";
import { closeCreateQuestionModel } from "../../../../modules/admin-modules/models/Actions";
import {
  createQuestionBankRequest,
  editQuestionBankRequest,
} from "../../../../modules/admin-modules/questions-bank/Actions";
import { getAllTagsRequest } from "../../../../modules/admin-modules/tags/Actions";

import "react-quill/dist/quill.snow.css";
import katex from 'katex';
import 'katex/dist/katex.min.css';

function ClickableImageThumbnail({ src, alt, label, onPreview }) {
  return (
    <Box
      as="button"
      type="button"
      onClick={() => onPreview(src, label)}
      alignSelf="flex-start"
      borderWidth="1px"
      borderColor="var(--border-subtle)"
      borderRadius="12px"
      overflow="hidden"
      cursor="pointer"
      p={1}
      bg="var(--surface-muted)"
      _hover={{ borderColor: brandPalette.primary, boxShadow: "md" }}
      transition="border-color 0.15s ease, box-shadow 0.15s ease"
    >
      <Image src={src} alt={alt} maxW="200px" maxH="160px" objectFit="contain" display="block" />
    </Box>
  );
}

const AddQuestion = ({ isAdd, data, setSelectedData }) => {
  const baseUrl = "https://edutopiaonline.com/api";

  const dispatch = useDispatch();

  const [selected, setSelected] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [questionAnswerImg, setQuestionAnswerImg] = useState("");
  const [cambridgeImage, setCambridgeImage] = useState("");
  const { isOpen: isPreviewOpen, onOpen: onPreviewOpen, onClose: onPreviewClose } = useDisclosure();
  const [previewImage, setPreviewImage] = useState(null);

  const openImagePreview = (src, label) => {
    setPreviewImage({ src, label });
    onPreviewOpen();
  };

  const openQuestions = useSelector((state) => state.model.isOpen16);
  const chapters = useSelector((state) => state.chapters.chapters);
  const tags = useSelector((state) => state.adminTags.tags);
  const isLoading = useSelector((state) => state.questionsBank.isLoading);
  useEffect(() => {
    if (openQuestions) {
      dispatch(getAllChaptersRequest());
      dispatch(getAllTagsRequest());
    }
  }, [openQuestions]);

  useEffect(() => {
    if (isAdd) {
      setSelectedTags([]);
    }
  }, [isAdd, data]);
  const chaptersOptions = chapters
    ?.filter((c) => Number(c?.active) === 0) // filter active == 0
    .map((c) => ({ value: c.id, label: c.name }));

  const tagsOptions = tags?.map((data) => {
    return { value: data.id, label: data.name };
  })

  const selectStyles = adminSelectStyles({ menuZIndex: 50 });

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
    reset,
  } = useForm({
    defaultValues: !isAdd ? data : { difficulty_level: "1", mark: 1 },
    shouldUnregister: true,
  });

  // Set tags when editing (must run after useForm so setValue is available)
  useEffect(() => {
    if (!isAdd && data?.tags) {
      const tagOptions = data.tags.map((t) => ({ value: t.id, label: t.name }));
      setSelectedTags(tagOptions);
      tagOptions.forEach((tag, idx) => setValue(`tags[${idx}]`, tag.value));
      const maxTags = 10;
      for (let i = tagOptions.length; i < maxTags; i++) {
        setValue(`tags[${i}]`, undefined);
      }
    }
  }, [isAdd, data, setValue]);

  const header = (
    <Box>
      <Heading
        fontSize="32px"
        fontWeight="800"
        color="var(--text-primary)"
        fontFamily="'Amiri','Tajawal',sans-serif"
      >
        {isAdd ? "Add Question" : "Edit Question"}
      </Heading>
      <Text
        fontSize="xs"
        mt={1}
        color="var(--text-muted)"
        textTransform="uppercase"
        letterSpacing="0.14em"
        fontWeight="700"
      >
        Stem, chapter, and tags
      </Text>
    </Box>
  );

  const onSubmit = async (val) => {
    const values = {
      ...val,
      difficulty_level: val?.difficulty_level ? val?.difficulty_level : "0",
    };
    const action = () => {
      dispatch(closeCreateQuestionModel());
      setSelectedData({});
      reset();
    };

    if (isAdd) {
      let formData = await new FormData();
      
      // Handle all form values except tags
      for (let key in values) {
        if (key !== 'tags') {
          if (key === 'url' && (values[key] == null || values[key] === '')) continue;
          formData.append(key, values[key]);
        }
      }
      
      // Handle tags: send only defined tag ids; when empty, omit tags so backend doesn't insert
      const tagIds = Object.values(values.tags || {}).filter(
        (v) => v !== undefined && v !== ''
      );
      if (tagIds.length > 0) {
        tagIds.forEach((id, idx) => formData.append(`tags[${idx}]`, id));
      }

      if (values.chapter_id) {
        await dispatch(createQuestionBankRequest(formData, action));
      } else {
        toast.error("Select a chapter.");
      }
    } else {
      let formData = await new FormData();
      const keys = [
        "select",
        "answers",
        "index",
        "delete",
        "edit",
        "chapter",
        "bookmark",
        "Actions",
        "deleted_at",

      ];
      formData.append("_method", "PUT");

      const totel = { ...data, ...values };

      for (let key in totel) {
        if (!keys.includes(key) && key !== 'tags') {
          if (key === 'url' && (totel[key] == null || totel[key] === '')) continue;
          formData.append(key, totel[key]);
        }
      }
      // Handle tags from form: send only defined tag ids; when empty, omit so backend can clear pivots
      const editTagIds = Object.values(values.tags || {}).filter(
        (v) => v !== undefined && v !== ''
      );
      if (editTagIds.length > 0) {
        editTagIds.forEach((id, idx) => formData.append(`tags[${idx}]`, id));
      }
      dispatch(
        editQuestionBankRequest({
          id: data?.id,
          formData: formData,
          action: action,
        })
      );
    }
  };
  const closeFunc = () => {
    dispatch(closeCreateQuestionModel());
    setSelectedData({});
    reset();
  };
  const toolbarOptions = [
    ['bold', 'italic', 'underline', 'strike'],        // toggled buttons
    ['blockquote', 'code-block'],
    ['formula'],
  
    [{ 'header': 1 }, { 'header': 2 }],               // custom button values
    [{ 'list': 'ordered'}, { 'list': 'bullet' }, { 'list': 'check' }],
    [{ 'script': 'sub'}, { 'script': 'super' }],      // superscript/subscript
    [{ 'indent': '-1'}, { 'indent': '+1' }],          // outdent/indent
    [{ 'direction': 'rtl' }],                         // text direction
  
    [{ 'size': ['small', false, 'large', 'huge'] }],  // custom dropdown
    [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
  
    [{ 'color': [] }, { 'background': [] }],          // dropdown with defaults from theme
    [{ 'font': [] }],
    [{ 'align': [] }],
  
    ['clean']                                         // remove formatting button
  ];

  const quillRef = useRef(null);

useEffect(() => {
  if (quillRef.current) {
    // Initialize MathQuill after the component mounts
    const enableMathQuillFormulaAuthoring = MathQuill4Quill({ Quill: ReactQuill.Quill, katex: window.katex });
    enableMathQuillFormulaAuthoring(quillRef.current.getEditor(), {
      operators: [
        ["\\sqrt{x}", "\\sqrt"],
        ["\\sqrt[n]{x}", "\\nthroot"],
        ["x^2", "^"],
        ["x_n", "_"],
        ["\\frac{x}{y}", "\\frac"],
        ["\\int_a^b", "\\int"],
        ["\\sum_{i=0}^n", "\\sum"],
      ]
    });
  }
}, [quillRef]);

// Make katex available globally for Quill
window.katex = katex;

// Register formula module if needed
if (ReactQuill.Quill) {
const Quill = ReactQuill.Quill;

// Only register if not already registered
if (!Quill.imports['formats/formula']) {
const Formula = Quill.import('formats/formula');
Quill.register(Formula, true);
}
}
  return (
    <>
    <MainModel
      isLoading={isLoading}
      header={header}
      closeFunc={closeFunc}
      openVar={openQuestions}
      buttonTitle={null}
      buttonFunc={handleSubmit(onSubmit)}
      size="5xl"
      contentProps={adminModalContentProps}
      headerProps={adminModalHeaderProps}
      bodyProps={adminModalBodyProps}
      hideDefaultFooter
    >
      <form onSubmit={handleSubmit(onSubmit)}>
        <Stack spacing={6} w="100%" dir="ltr">
          <Box w="100%">
            <FormLabel
              m={0}
              mb={2}
              fontSize="14px"
              fontWeight="700"
              color="var(--text-primary)"
            >
              Passage (optional)
            </FormLabel>
            <Textarea
              name="comprehension"
              placeholder="Reading passage before the question"
              defaultValue={data?.comprehension}
              bg="var(--surface-muted)"
              color="var(--text-primary)"
              borderRadius="12px"
              border="1px solid"
              borderColor="var(--border-subtle)"
              {...register("comprehension")}
            />
          </Box>
          <Box w="100%">
            <Text fontWeight="700" color="var(--text-primary)" mb={2}>
              Question body
            </Text>
            <ReactQuill
              theme="snow"
              register={register}
              name="question"
              defaultValue={data?.question}
              onChange={(val) => setValue("question", val)}
              modules={{ 
                toolbar: toolbarOptions,
                formula: true  // Enable formula module
              }}
            />
          </Box>
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="100%">
            <MainInput
              label="Marks"
              defaultValues={data?.mark || 1}
              error={errors?.mark?.message}
              placeholder="Question marks"
              register={register}
              name="mark"
              type="number"
              required={false}
            />
            <MainInput
              label="URL (optional)"
              defaultValues={data?.url}
              error={errors?.url?.message}
              placeholder="https://..."
              register={register}
              name="url"
              type="url"
              required={false}
            />
          </SimpleGrid>
          <VStack align="stretch" spacing={3} w="100%" my="20px">
            <HStack flexWrap="wrap" align="center">
              <FormLabel fontWeight="700" color="var(--text-primary)" whiteSpace="nowrap" m={0}>
                Question image
              </FormLabel>
              <Button
                bg={brandPalette.primary}
                color="white"
                borderRadius="12px"
                textAlign="center"
                position="relative"
                _hover={{ bg: brandPalette.accentDark }}
              >
                <Text>
                  {selected
                    ? selected
                    : data?.image
                      ? `file_1`
                      : "Choose file"}
                </Text>
                <Input
                  onChange={(e) => {
                    setValue("image", e.target.files[0]);
                    setSelected((s) => e.target.files[0].name);
                  }}
                  type="file"
                  placeholder="اختر ملف ليتم رفعه"
                  opacity="0"
                  position="absolute"
                  w="100%"
                  h="100%"
                />
              </Button>
            </HStack>
            {data?.image ? (
              <ClickableImageThumbnail
                src={`${baseUrl}/${data?.image}`}
                alt="Question image"
                label="Question image"
                onPreview={openImagePreview}
              />
            ) : null}
          </VStack>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="100%" my="20px">
            <VStack align="stretch" spacing={3}>
              <HStack flexWrap="wrap" align="center">
                <FormLabel fontWeight="700" color="var(--text-primary)" whiteSpace="nowrap" m={0}>
                  Model answer image
                </FormLabel>
                <Button
                  bg={brandPalette.primary}
                  color="white"
                  borderRadius="12px"
                  textAlign="center"
                  position="relative"
                  _hover={{ bg: brandPalette.accentDark }}
                >
                  <Text>
                    {questionAnswerImg
                      ? questionAnswerImg
                      : data?.model_answer_image
                        ? `file_1`
                        : "Choose file"}
                  </Text>
                  <Input
                    onChange={(e) => {
                      setValue("model_answer_image", e.target.files[0]);
                      setQuestionAnswerImg((s) => e.target.files[0].name);
                    }}
                    type="file"
                    placeholder="اختر ملف ليتم رفعه"
                    opacity="0"
                    position="absolute"
                    w="100%"
                    h="100%"
                  />
                </Button>
              </HStack>
              {data?.model_answer_image ? (
                <ClickableImageThumbnail
                  src={`${baseUrl}/${data?.model_answer_image}`}
                  alt="Model answer image"
                  label="Model answer image"
                  onPreview={openImagePreview}
                />
              ) : null}
            </VStack>

            <VStack align="stretch" spacing={3}>
              <HStack flexWrap="wrap" align="center">
                <FormLabel fontWeight="700" color="var(--text-primary)" whiteSpace="nowrap" m={0}>
                  Cambridge model answer image
                </FormLabel>
                <Button
                  bg={brandPalette.primary}
                  color="white"
                  borderRadius="12px"
                  textAlign="center"
                  position="relative"
                  _hover={{ bg: brandPalette.accentDark }}
                >
                  <Text>
                    {cambridgeImage
                      ? cambridgeImage
                      : data?.cambridge_image
                        ? `file_1`
                        : "Choose file"}
                  </Text>
                  <Input
                    onChange={(e) => {
                      setValue("cambridge_image", e.target.files[0]);
                      setCambridgeImage((s) => e.target.files[0].name);
                    }}
                    type="file"
                    placeholder="اختر ملف ليتم رفعه"
                    opacity="0"
                    position="absolute"
                    w="100%"
                    h="100%"
                  />
                </Button>
              </HStack>
              {data?.cambridge_image ? (
                <ClickableImageThumbnail
                  src={`${baseUrl}/${data?.cambridge_image}`}
                  alt="Cambridge model answer image"
                  label="Cambridge model answer image"
                  onPreview={openImagePreview}
                />
              ) : null}
            </VStack>
          </SimpleGrid>

          <Box w="100%">
            <FormLabel fontWeight="700" color="var(--text-primary)" mb={2}>
              Difficulty / source
            </FormLabel>

            <Stack
              direction="row"
              role="group"
              aria-labelledby="my-radio-group"
              spacing="20px"
              flexWrap="wrap"
            >
              <label>
                <input
                  {...register("difficulty_level")}
                  type="radio"
                  value="1"
                  defaultChecked={data?.difficulty_level == 1 ? true : false}
                />
                Easy
              </label>
              <label>
                <input
                  {...register("difficulty_level")}
                  type="radio"
                  value="2"
                  defaultChecked={data?.difficulty_level == 2 ? true : false}
                />
                Medium
              </label>
              <label>
                <input
                  {...register("difficulty_level")}
                  type="radio"
                  value="3"
                  defaultChecked={data?.difficulty_level == 3 ? true : false}
                />
                Hard
              </label>
            </Stack>
          </Box>

          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6} w="100%">
            <FormControl>
              <VStack align="stretch" spacing={2}>
                <FormLabel
                  m={0}
                  fontSize="14px"
                  fontWeight="700"
                  color="var(--text-primary)"
                  textAlign="left"
                >
                  Chapter
                </FormLabel>
                <Select
                  options={chaptersOptions}
                  onChange={(d) => setValue("chapter_id", d.value)}
                  styles={selectStyles}
                  placeholder="Select chapter"
                  defaultValue={{
                    label: data?.chapter?.name,
                    value: data?.chapter?.id,
                  }}
                />
              </VStack>
            </FormControl>

            <FormControl>
              <VStack align="stretch" spacing={2}>
                <FormLabel
                  m={0}
                  fontSize="14px"
                  fontWeight="700"
                  color="var(--text-primary)"
                  textAlign="left"
                >
                  Tags
                </FormLabel>
                <Select
                  options={tagsOptions}
                  isMulti
                  value={selectedTags}
                  onChange={(d) => {
                    setSelectedTags(d || []);
                    const maxTags = 10;
                    for (let i = 0; i < maxTags; i++) {
                      setValue(`tags[${i}]`, undefined);
                    }
                    (d || []).forEach((tag, idx) => {
                      setValue(`tags[${idx}]`, tag.value);
                    });
                  }}
                  styles={selectStyles}
                  placeholder="Tags"
                />
              </VStack>
            </FormControl>
          </SimpleGrid>


          <Flex justify="flex-end" gap={6} pt={4}>
            <Button
              variant="ghost"
              color="var(--text-primary)"
              fontWeight="800"
              _hover={{ textDecoration: "underline", bg: "rgba(91, 13, 22, 0.08)" }}
              onClick={closeFunc}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              bg={brandPalette.primary}
              color="white"
              borderRadius="lg"
              px={8}
              py={6}
              fontWeight="800"
              isLoading={isLoading}
              _hover={{ bg: brandPalette.accentDark }}
              boxShadow="0 10px 24px rgba(91, 13, 22, 0.18)"
            >
              Save Changes
            </Button>
          </Flex>
        </Stack>
      </form>
    </MainModel>

    <Modal isOpen={isPreviewOpen} onClose={onPreviewClose} size="4xl" isCentered>
      <ModalOverlay bg="blackAlpha.700" />
      <ModalContent bg="var(--surface-raised)" maxW="min(90vw, 960px)">
        <ModalHeader color="var(--text-primary)">{previewImage?.label}</ModalHeader>
        <ModalCloseButton />
        <ModalBody pb={6} display="flex" justifyContent="center">
          {previewImage?.src ? (
            <Image
              src={previewImage.src}
              alt={previewImage.label}
              maxH="75vh"
              maxW="100%"
              objectFit="contain"
              mx="auto"
            />
          ) : null}
        </ModalBody>
      </ModalContent>
    </Modal>
    </>
  );
};

export default AddQuestion;


